import type { PortInfoPort } from '~hooks/queries/port-info/use-get-port-info-ports'
import type {
  ContinentLocation,
  CountryLocation,
} from '~hooks/queries/port-info/use-get-locations'

export type PortInfoType = 'port' | 'country' | 'continent'

export type PortInfoOperation =
  'LOAD' | 'DISCHARGE' | 'LAYBY' | 'CLEANING' | 'STS' | 'OTHER'

export type PortInfoCargoType = 'CHEMICALS' | 'CPP' | 'DPP' | 'GAS'

export type PortInfoIspsSecurityLevel = '1' | '2' | '3'

export interface PortInfoFile {
  _id: string
  title: string
  field: string
  name: string
  fileSize: string
  signedUrl: string
  portInfoId: string
  type: string
  uploadedBy: string
  uploadedDate: string
}

export interface AttachedFile {
  id: string
  name: string
  fileSize: string
  signedUrl: string
}

export const REMARK_FIELDS = [
  { label: 'Country Remarks', key: 'generalRemarksCountry' },
  { label: 'Port Remarks', key: 'generalRemarksPort' },
  { label: 'Berth/Terminal Remarks', key: 'generalRemarksBerthTerminal' },
  { label: 'Depth Alongside Remarks', key: 'generalRemarksDepthAlongside' },
  { label: 'Tide/Weather Remarks', key: 'generalRemarksTideWeather' },
  { label: 'Approach/Pilot Remarks', key: 'generalRemarksApproachPilot' },
  { label: 'Tugs Remarks', key: 'generalRemarksTugs' },
  { label: 'Berthing Remarks', key: 'generalRemarksBerthing' },
  { label: 'Mooring Remarks', key: 'generalRemarksMooring' },
  { label: 'Authorities Remarks', key: 'generalRemarksAuthorities' },
  { label: 'Security Remarks', key: 'generalRemarksSecurity' },
  { label: 'Cargo Operations Remarks', key: 'generalRemarksCargoOperations' },
  { label: 'Departure Remarks', key: 'generalRemarksDeparture' },
  { label: 'Other Remarks', key: 'generalRemarksOther' },
] as const

export interface PortSelection {
  port: PortInfoPort
  portName: string
}

export interface ContinentSelection {
  continent: ContinentLocation
  continentName: string
}

export interface CountrySelection {
  country: CountryLocation
  countryName: string
}

export interface PortInfoFormData {
  type?: PortInfoType
  continent?: ContinentSelection
  country?: CountrySelection
  subject?: string
  remarks?: string
  dateOfInformation?: Date
  port?: PortSelection
  gisisCode?: string
  terminalName?: string
  berthName?: string
  vesselName?: string
  imoNumber?: string
  pool?: string
  dateOfArrival?: Date
  dateOfDeparture?: Date
  arrivalDraft?: number
  departureDraft?: number
  airDraft?: number
  operation?: PortInfoOperation
  cargoType?: string
  ispsSecurityLevel?: string
  cargoConnections?: string
  manifoldPressure?: number
  loadDischargeRate?: number | null
  freshWaterAvailable?: string | null
  garbageLandingAvailable?: string | null
  slopsSludgeLandingAvailable?: string | null
  deSloppingAvailable?: string | null
  dockWaterDensity?: number | null
  generalRemarksCountry?: string | null
  generalRemarksPort?: string | null
  generalRemarksBerthTerminal?: string | null
  generalRemarksDepthAlongside?: string | null
  generalRemarksTideWeather?: string | null
  generalRemarksApproachPilot?: string | null
  generalRemarksTugs?: string | null
  generalRemarksBerthing?: string | null
  generalRemarksMooring?: string | null
  generalRemarksAuthorities?: string | null
  generalRemarksSecurity?: string | null
  generalRemarksCargoOperations?: string | null
  generalRemarksDeparture?: string | null
  generalRemarksOther?: string | null
}

export interface PageData {
  formData: PortInfoFormData
  updateFormData: (data: PortInfoFormData) => void
  portInfoId?: string
  files?: PortInfoFile[]
  missingFields?: (keyof PortInfoFormData)[]
}

export interface PortInfoListItem {
  _id: string
  type?: PortInfoType
  portName?: string
  portCode?: string
  portCountry?: string
  gisisCode?: string
  terminalName?: string
  berthName?: string
  dateOfArrival?: string
  dateOfInformation?: string
  continentName?: string
  continentCode?: string
  countryName?: string
  countryCode?: string
  subject?: string
  remarks?: string
  vesselName?: string
  imoNumber?: string
  pool?: string
}

export interface PortInfoItem extends PortInfoListItem {
  vesselName: string
  imoNumber: string
  dateOfDeparture: string
  arrivalDraft: number
  departureDraft: number
  airDraft?: number | null
  operation: PortInfoOperation
  cargoType: string
  ispsSecurityLevel: string
  cargoConnections: string
  manifoldPressure: number
  loadDischargeRate: number | null
  freshWaterAvailable: string | null
  garbageLandingAvailable: string | null
  slopsSludgeLandingAvailable: string | null
  deSloppingAvailable: string | null
  dockWaterDensity: number | null
  generalRemarksCountry: string | null
  generalRemarksPort: string | null
  generalRemarksBerthTerminal: string | null
  generalRemarksDepthAlongside: string | null
  generalRemarksTideWeather: string | null
  generalRemarksApproachPilot: string | null
  generalRemarksTugs: string | null
  generalRemarksBerthing: string | null
  generalRemarksMooring: string | null
  generalRemarksAuthorities: string | null
  generalRemarksSecurity: string | null
  generalRemarksCargoOperations: string | null
  generalRemarksDeparture: string | null
  generalRemarksOther: string | null
  updatedBy: string
  updatedAt: string
  files: PortInfoFile[]
}
